import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAttestation,
  voidAttestation,
  formatAssertedDate,
  type AttestationRecord,
} from "@/lib/attestations";
import { ArrowLeft, Printer, FileSignature, Trash2, Ban } from "lucide-react";

// Legal text v1-2025. If AUTHORIZATION_TEXT_VERSION on the backend ever changes,
// add a new branch here keyed by attestation.authorization_text_version so each
// archived signing renders exactly as it was signed.
function AuthorizationTextV1({
  rec,
}: {
  rec: AttestationRecord;
}) {
  const entity = rec.entity_name || "[Entity Name]";
  const last4 = rec.tin_last4 || "XXXX";
  const year = rec.program_year;
  const clinician = rec.clinician_name || "[Clinician Name]";
  const npi = rec.npi || "[NPI]";

  if (rec.evaluation_type === "GROUP") {
    return (
      <>
        <p>
          {entity} (TIN ending **-{last4}) authorizes A Value-Based Care Registry, a
          CMS-designated Qualified Clinical Data Registry (QCDR), to submit {year} MIPS
          performance data to the Centers for Medicare &amp; Medicaid Services (CMS) on its
          behalf.
        </p>
        <p>
          This authorization covers submission of performance data for all patients regardless of
          payer across the selected MIPS performance categories and measures for the {year}{" "}
          performance year, in accordance with 42 CFR § 414.1400.
        </p>
        <p>
          The signatory represents that they hold full authority to execute this authorization on
          behalf of the identified entity.
        </p>
      </>
    );
  }

  return (
    <>
      <p>
        {clinician} (NPI: {npi}), affiliated with {entity} (TIN ending **-{last4}), authorizes A
        Value-Based Care Registry, a CMS-designated Qualified Clinical Data Registry (QCDR), to
        submit {year} MIPS performance data to the Centers for Medicare &amp; Medicaid Services
        (CMS) on their behalf.
      </p>
      <p>
        This authorization covers submission of performance data for all patients regardless of
        payer across the selected MIPS performance categories and measures for the {year}{" "}
        performance year, in accordance with 42 CFR § 414.1400.
      </p>
      <p>
        {clinician} represents that they are the eligible clinician identified herein and holds
        full authority to execute this authorization.
      </p>
    </>
  );
}

// Minimal HTML escape so user-entered strings (signer name, reason, etc.)
// can never inject markup into the print window.
function esc(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function authorizationBodyHtml(rec: AttestationRecord): string {
  const entity = esc(rec.entity_name) || "[Entity Name]";
  const last4 = esc(rec.tin_last4) || "XXXX";
  const year = rec.program_year;
  if (rec.evaluation_type === "GROUP") {
    return `
      <p>${entity} (TIN ending **-${last4}) authorizes A Value-Based Care Registry, a CMS-designated Qualified Clinical Data Registry (QCDR), to submit ${year} MIPS performance data to the Centers for Medicare &amp; Medicaid Services (CMS) on its behalf.</p>
      <p>This authorization covers submission of performance data for all patients regardless of payer across the selected MIPS performance categories and measures for the ${year} performance year, in accordance with 42 CFR &sect; 414.1400.</p>
      <p>The signatory represents that they hold full authority to execute this authorization on behalf of the identified entity.</p>
    `;
  }
  const clinician = esc(rec.clinician_name) || "[Clinician Name]";
  const npi = esc(rec.npi) || "[NPI]";
  return `
    <p>${clinician} (NPI: ${npi}), affiliated with ${entity} (TIN ending **-${last4}), authorizes A Value-Based Care Registry, a CMS-designated Qualified Clinical Data Registry (QCDR), to submit ${year} MIPS performance data to the Centers for Medicare &amp; Medicaid Services (CMS) on their behalf.</p>
    <p>This authorization covers submission of performance data for all patients regardless of payer across the selected MIPS performance categories and measures for the ${year} performance year, in accordance with 42 CFR &sect; 414.1400.</p>
    <p>${clinician} represents that they are the eligible clinician identified herein and holds full authority to execute this authorization.</p>
  `;
}

// Build a fully self-contained HTML document suitable for printing in a new
// window. Avoids depending on the React app's stylesheets or layout so the
// print output is consistent across browsers.
function buildPrintHtml(rec: AttestationRecord): string {
  const cats = rec.categories_submitted || {
    data_freeze: true,
    quality: false,
    ia: false,
    pi: false,
  };
  const categoryNames = [
    cats.quality ? "Quality" : null,
    cats.ia ? "Improvement Activities" : null,
    cats.pi ? "Promoting Interoperability" : null,
  ]
    .filter(Boolean)
    .map((s) => esc(s))
    .join(", ");

  const voidedBanner = rec.voided_at
    ? `
      <div class="voided-banner">
        <div class="voided-title">This authorization has been voided.</div>
        <div class="voided-meta">
          Voided on ${esc(formatAssertedDate(rec.voided_at))}${
        rec.voided_reason
          ? ` &mdash; reason: <em>&ldquo;${esc(rec.voided_reason)}&rdquo;</em>`
          : ""
      }. The row is retained for audit but is no longer the active signing.
        </div>
      </div>
    `
    : "";

  const npiBlock =
    rec.evaluation_type === "NPI"
      ? `
        <tr><th>Clinician name</th><td>${esc(rec.clinician_name) || "&mdash;"}</td></tr>
        <tr><th>NPI</th><td>${esc(rec.npi) || "&mdash;"}</td></tr>
      `
      : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Signed Authorization #${rec.id} &ndash; ${rec.program_year} MIPS</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap" rel="stylesheet">
<style>
  @page { margin: 0.6in; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #0f172a;
    background: #ffffff;
    line-height: 1.6;
    font-size: 13px;
    padding: 32px 36px;
  }
  .doc-header { border-bottom: 1px solid #cbd5e1; padding-bottom: 14px; margin-bottom: 22px; }
  .doc-eyebrow { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #64748b; }
  .doc-title { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 4px; }
  .doc-title h1 { font-size: 22px; font-weight: 700; margin: 0; }
  .badge { display: inline-block; font-size: 11px; padding: 2px 8px; border: 1px solid #cbd5e1; border-radius: 9999px; }
  .badge-secondary { background: #e2e8f0; }
  .badge-ok { border-color: #6ee7b7; background: #ecfdf5; color: #065f46; }
  .doc-sub { font-size: 12px; color: #64748b; margin-top: 4px; }
  .voided-banner {
    display: block;
    margin: 0 0 18px;
    padding: 12px 14px;
    border: 1px solid #fca5a5;
    background: #fef2f2;
    color: #991b1b;
    border-radius: 6px;
  }
  .voided-title { font-weight: 700; font-size: 14px; }
  .voided-meta { font-size: 11px; margin-top: 4px; }
  .section { margin-bottom: 18px; page-break-inside: avoid; }
  .section h2 {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 6px;
  }
  .section p { margin: 0 0 10px; }
  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 24px;
  }
  .info-grid table { width: 100%; border-collapse: collapse; }
  .info-grid th {
    text-align: left;
    font-weight: 500;
    font-size: 11px;
    color: #64748b;
    padding: 2px 0;
    width: 40%;
  }
  .info-grid td { font-size: 13px; color: #0f172a; padding: 2px 0; }
  .callout {
    border: 1px solid #fde68a;
    background: #fffbeb;
    color: #78350f;
    padding: 10px 12px;
    border-radius: 4px;
    font-size: 11.5px;
    line-height: 1.55;
    margin: 12px 0;
  }
  .retention {
    border-left: 3px solid #cbd5e1;
    padding: 4px 0 4px 12px;
    color: #475569;
    font-size: 11px;
    line-height: 1.55;
  }
  .signature-block {
    border: 2px solid #0f172a;
    border-radius: 6px;
    padding: 20px 22px;
    margin-top: 8px;
  }
  .signature-line { border-bottom: 1.5px solid #0f172a; padding-bottom: 6px; }
  .signature-cursive { font-family: "Dancing Script", Georgia, serif; font-size: 30px; line-height: 1.1; }
  .sig-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px 22px; margin-top: 16px; }
  .sig-grid .label { font-size: 11px; color: #64748b; margin-bottom: 4px; }
  .sig-grid .value { font-size: 13px; color: #0f172a; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; }
  .doc-footer { margin-top: 22px; font-size: 10px; color: #94a3b8; text-align: center; }
</style>
</head>
<body>
  <div class="doc-header">
    <div class="doc-eyebrow">CMS MIPS Authorization</div>
    <div class="doc-title">
      <h1>Signed Authorization</h1>
      <span class="badge badge-secondary">${rec.evaluation_type === "GROUP" ? "Group / TIN" : "Individual / NPI"}</span>
      <span class="badge badge-ok">Signed and recorded</span>
    </div>
    <div class="doc-sub">Performance year ${rec.program_year}</div>
  </div>

  ${voidedBanner}

  <section class="section">
    <h2>Reporting entity</h2>
    <div class="info-grid">
      <table>
        <tr><th>Legal entity name</th><td>${esc(rec.entity_name)}</td></tr>
        <tr><th>TIN (last 4)</th><td>**-${esc(rec.tin_last4) || "XXXX"}</td></tr>
        ${npiBlock}
      </table>
      <table>
        <tr><th>Program year</th><td>${rec.program_year}</td></tr>
        <tr><th>Submission type</th><td>${rec.evaluation_type}</td></tr>
      </table>
    </div>
  </section>

  <section class="section">
    <h2>Submission scope</h2>
    <p><strong>Data Freeze:</strong> Acknowledged</p>
    <p><strong>Categories submitted:</strong> ${categoryNames || "<em>None</em>"}</p>
  </section>

  <section class="section">
    <h2>Authorization</h2>
    ${authorizationBodyHtml(rec)}
    <div class="callout">
      Certification: By signing below, the ${rec.evaluation_type === "GROUP" ? "signatory" : "clinician"} certifies that all data submitted to CMS
      ${rec.evaluation_type === "GROUP" ? "on behalf of this entity" : "on their behalf"} is true, accurate, and complete to the best of their knowledge. If any submitted information is
      found to be inaccurate or incomplete, corrected information will be provided promptly. The ${rec.evaluation_type === "GROUP" ? "signatory" : "clinician"} understands
      that the knowing omission, misrepresentation, or falsification of any submitted information may be subject to
      criminal, civil, or administrative penalties, including fines, civil damages, and/or imprisonment,
      under applicable federal law.
    </div>
    <div class="retention">
      Record retention: Per 42 CFR &sect; 414.1400(b)(3)(xiii)(C), this authorization will be retained by
      A Value-Based Care Registry for six (6) years following the ${rec.program_year} performance year.
    </div>
  </section>

  <section class="section">
    <h2>Signature</h2>
    <div class="signature-block">
      <div class="signature-line">
        <span class="signature-cursive">${esc(rec.signer_name)}</span>
      </div>
      <div class="sig-grid">
        <div>
          <div class="label">Signed by (printed)</div>
          <div class="value">${esc(rec.signer_name)}</div>
        </div>
        <div>
          <div class="label">Title / role</div>
          <div class="value">${esc(rec.signer_title_role)}</div>
        </div>
        <div>
          <div class="label">Date signed</div>
          <div class="value">${esc(formatAssertedDate(rec.signed_date_asserted))}</div>
        </div>
      </div>
    </div>
  </section>

  <div class="doc-footer">Authorization #${rec.id} &middot; A Value-Based Care Registry (CMS-designated QCDR)</div>

  <script>
    window.addEventListener("load", function () {
      // Give web fonts a moment to load before printing.
      setTimeout(function () { window.focus(); window.print(); }, 300);
      window.onafterprint = function () { window.close(); };
    });
  </script>
</body>
</html>`;
}

export default function SubmitCMSViewAttestation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedPractice } = useAuth();
  const { toast } = useToast();

  const [rec, setRec] = useState<AttestationRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);

  useEffect(() => {
    if (!id || !selectedPractice?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAttestation(id, selectedPractice.id)
      .then((data) => {
        if (!cancelled) setRec(data);
      })
      .catch((err) => {
        console.error("[SubmitCMSViewAttestation] load failed:", err);
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, selectedPractice?.id]);

  const handlePrint = () => {
    if (!rec) return;
    // Use a hidden iframe so the print dialog appears in-place — no visible new
    // tab, no inherited favicon, no pop-up blocker. The iframe's document is
    // fully isolated from the app's styles and layout.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    document.body.appendChild(iframe);

    const cleanup = () => {
      try {
        document.body.removeChild(iframe);
      } catch {
        /* already removed */
      }
    };

    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (!win) {
        cleanup();
        return;
      }
      // Give web fonts a beat to load before opening the print dialog.
      setTimeout(() => {
        try {
          win.focus();
          win.print();
        } finally {
          // Most browsers fire afterprint synchronously after the dialog closes;
          // we also schedule a fallback removal in case the event never fires.
          win.onafterprint = cleanup;
          setTimeout(cleanup, 60_000);
        }
      }, 300);
    };

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      cleanup();
      toast({
        title: "Could not open print view",
        description: "Your browser blocked the print frame.",
        variant: "destructive",
      });
      return;
    }
    doc.open();
    // Strip the auto-print script from the standalone HTML — we drive printing
    // from the parent so we control cleanup.
    const html = buildPrintHtml(rec).replace(
      /<script>[\s\S]*?<\/script>/,
      "",
    );
    doc.write(html);
    doc.close();
  };

  const handleVoidConfirm = async () => {
    if (!rec || !selectedPractice) return;
    const reason = voidReason.trim();
    if (!reason) {
      toast({
        title: "Reason required",
        description: "Please provide a brief reason for voiding this authorization.",
        variant: "destructive",
      });
      return;
    }
    setVoiding(true);
    try {
      await voidAttestation(rec.id, reason, selectedPractice.id);
      toast({
        title: "Authorization voided",
        description:
          "The authorization has been marked voided and is no longer the active signing.",
      });
      setVoidOpen(false);
      setVoidReason("");
      // Reload the record so the voided banner appears.
      const fresh = await getAttestation(rec.id, selectedPractice.id);
      setRec(fresh);
    } catch (err) {
      console.error("[SubmitCMSViewAttestation] void failed:", err);
      toast({
        title: "Could not void authorization",
        description: err instanceof Error ? err.message : "Unexpected error",
        variant: "destructive",
      });
    } finally {
      setVoiding(false);
    }
  };

  const shellClasses =
    "-m-6 min-h-[calc(100vh-3.5rem)] bg-gradient-to-br from-slate-50 to-slate-100 p-6 dark:from-slate-900 dark:to-slate-800";

  if (!selectedPractice) {
    return (
      <div className={shellClasses}>
        <Card className="shadow-sm">
          <CardContent className="py-8 text-center text-muted-foreground">
            Select a practice to view this authorization.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={shellClasses}>
        <Card className="shadow-sm">
          <CardContent className="py-8 text-center text-muted-foreground">Loading…</CardContent>
        </Card>
      </div>
    );
  }

  if (error || !rec) {
    return (
      <div className={shellClasses}>
        <Button variant="ghost" size="sm" onClick={() => navigate("/submit")} className="mb-2 -ml-2">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to Submit to CMS
        </Button>
        <Card className="border-destructive/50 bg-destructive/5 shadow-sm">
          <CardContent className="py-8 text-center text-sm">
            {error || "Authorization not found."}
          </CardContent>
        </Card>
      </div>
    );
  }

  const cats = rec.categories_submitted || {
    data_freeze: true,
    quality: false,
    ia: false,
    pi: false,
  };
  const selectedCategoryNames = [
    cats.quality ? "Quality" : null,
    cats.ia ? "Improvement Activities" : null,
    cats.pi ? "Promoting Interoperability" : null,
  ].filter(Boolean) as string[];

  return (
    <div className="-m-6 min-h-[calc(100vh-3.5rem)] bg-gradient-to-br from-slate-50 to-slate-100 p-6 dark:from-slate-900 dark:to-slate-800">
      <Helmet>
        <title>{`Signed Authorization #${rec.id} – ${rec.program_year} MIPS`}</title>
        <link rel="canonical" href={`/submit/attestation/${rec.id}`} />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap');
        .attest-view-signature {
          font-family: 'Dancing Script', Georgia, serif;
          font-size: 38px;
          line-height: 1.1;
          color: hsl(var(--foreground));
        }
      `}</style>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/submit")} className="-ml-2">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to Submit to CMS
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {!rec.voided_at && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVoidOpen(true)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Void authorization
            </Button>
          )}
          <Button onClick={handlePrint} variant="outline" size="sm">
            <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      {/* Voided banner (also shown in print) */}
      {rec.voided_at && (
        <div className="mb-4 flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <Ban className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <div className="font-semibold">This authorization has been voided.</div>
            <div className="mt-0.5 text-xs">
              Voided on {formatAssertedDate(rec.voided_at)}
              {rec.voided_reason ? (
                <>
                  {" "}— reason: <span className="italic">"{rec.voided_reason}"</span>
                </>
              ) : null}
              . The row is retained for audit but is no longer the active signing.
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="mb-6 border-b pb-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            CMS MIPS Authorization
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold">Signed Authorization</h1>
            <Badge variant="secondary">
              {rec.evaluation_type === "GROUP" ? "Group / TIN" : "Individual / NPI"}
            </Badge>
            <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">
              Signed and recorded
            </Badge>
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            Performance year {rec.program_year}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left column: Authorization legal text */}
          <div className="space-y-4 lg:col-span-2">
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="text-base">Authorization</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-[15px] leading-7 [&_p+p]:mt-3">
                <AuthorizationTextV1 rec={rec} />

                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-6 text-amber-900">
                  Certification: By signing below, the{" "}
                  {rec.evaluation_type === "GROUP" ? "signatory" : "clinician"} certifies that all
                  data submitted to CMS{" "}
                  {rec.evaluation_type === "GROUP"
                    ? "on behalf of this entity"
                    : "on their behalf"}{" "}
                  is true, accurate, and complete to the best of their knowledge. If any submitted
                  information is found to be inaccurate or incomplete, corrected information will
                  be provided promptly. The{" "}
                  {rec.evaluation_type === "GROUP" ? "signatory" : "clinician"} understands that
                  the knowing omission, misrepresentation, or falsification of any submitted
                  information may be subject to criminal, civil, or administrative penalties,
                  including fines, civil damages, and/or imprisonment, under applicable federal
                  law.
                </div>

                <div className="border-l-2 border-muted pl-3 text-xs leading-6 text-muted-foreground">
                  Record retention: Per 42 CFR § 414.1400(b)(3)(xiii)(C), this authorization will
                  be retained by A Value-Based Care Registry for six (6) years following the{" "}
                  {rec.program_year} performance year.
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column: identity + scope + signature (sticky on lg+) */}
          <div className="space-y-4 lg:col-span-1 lg:sticky lg:top-4 lg:self-start">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Reporting entity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <ViewField label="Legal entity name" value={rec.entity_name} />
                  <ViewField label="TIN (last 4)" value={`**-${rec.tin_last4 || "XXXX"}`} />
                  {rec.evaluation_type === "NPI" && (
                    <>
                      <ViewField label="Clinician name" value={rec.clinician_name || "—"} />
                      <ViewField label="NPI" value={rec.npi || "—"} />
                    </>
                  )}
                  <ViewField label="Program year" value={String(rec.program_year)} />
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Submission scope</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Data Freeze:</span>{" "}
                  <Badge
                    variant="outline"
                    className="border-emerald-300 bg-emerald-50 text-emerald-800"
                  >
                    Acknowledged
                  </Badge>
                </div>
                <div>
                  <span className="font-medium">Categories submitted:</span>{" "}
                  {selectedCategoryNames.length > 0 ? (
                    <span>{selectedCategoryNames.join(", ")}</span>
                  ) : (
                    <span className="text-destructive">None</span>
                  )}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>

        {/* Signature block — prominent, full-width, document-style */}
        <Card className="mt-6 border-2 border-slate-300 shadow-md">
          <CardHeader className="border-b bg-slate-50/70 dark:bg-slate-900/50">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileSignature className="h-4 w-4 text-muted-foreground" /> Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="py-6">
            <div className="space-y-5">
              <div>
                <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                  Signed by
                </div>
                <div className="attest-view-signature border-b border-slate-400 pb-2">
                  {rec.signer_name}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <ViewField label="Signed by (printed)" value={rec.signer_name} />
                <ViewField label="Title / role" value={rec.signer_title_role} />
                <ViewField
                  label="Date signed"
                  value={formatAssertedDate(rec.signed_date_asserted)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Void confirmation dialog */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void this authorization?</DialogTitle>
            <DialogDescription>
              The row is retained in the database for audit, but it will be marked voided and
              hidden from the "latest signed" view. This cannot be undone — to replace it, sign a
              new authorization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="void-reason" className="text-sm">
              Reason <span className="text-destructive">(required)</span>
            </Label>
            <Textarea
              id="void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Wrong title entered; superseded by corrected signing"
              maxLength={500}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Captured in the audit trail alongside the void timestamp and the user who voided.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVoidOpen(false)} disabled={voiding}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleVoidConfirm}
              disabled={voiding || voidReason.trim().length === 0}
            >
              {voiding ? "Voiding…" : "Void authorization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ViewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mb-1 text-xs text-muted-foreground">{label}</div>
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}
